import { Component, lazy, Suspense, useState } from 'react';
import { useAppearanceSnapshot } from '../hooks/useAppearance';
import TenantPublicShell from './TenantPublicShell';
import { UserLayout } from './UserLayout';

function CustomerRouteSurface({ children }) {
  const appearance = useAppearanceSnapshot();
  if (!appearance?.customer || !appearance.data) return children;
  return appearance.signed
    ? <UserLayout>{children}</UserLayout>
    : <TenantPublicShell appearance={appearance.data}>{children}</TenantPublicShell>;
}

function PageError({ onRetry }) {
  const appearance = useAppearanceSnapshot();
  const customer = appearance?.customer === true;
  const content = <div role="alert" className={`max-w-md rounded-lg border p-6${customer ? '' : ' bg-white text-gray-900'}`}
    style={customer ? { background: 'var(--appearance-surface, transparent)', color: 'inherit', borderColor: 'var(--reference-border)' } : undefined}>
    <h1 className="text-xl font-semibold">This page could not load</h1>
    <p className="mt-2">Check your connection and try again. If the site was updated, reload the page.</p>
    <div className="mt-4 flex flex-wrap gap-3">
      <button type="button" className={`rounded px-4 py-2${customer ? '' : ' bg-blue-600 text-white'}`}
        style={customer ? { background: 'var(--appearance-primary, transparent)', color: 'var(--appearance-on-primary, inherit)' } : undefined}
        onClick={onRetry}>Try again</button>
      <button type="button" className="rounded border px-4 py-2" onClick={() => window.location.reload()}>Reload page</button>
    </div>
  </div>;
  return customer ? <CustomerRouteSurface><div className="flex min-h-[calc(100vh-124px)] items-center justify-center p-6">{content}</div></CustomerRouteSurface>
    : <main className="min-h-screen bg-gray-50 flex items-center justify-center p-6">{content}</main>;
}

class RoutePageBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return <PageError onRetry={this.props.onRetry} />;
  }
}

export function PageLoading() {
  const appearance = useAppearanceSnapshot();
  if (!appearance?.customer) return <div role="status" aria-live="polite" className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-700">Loading page…</div>;
  return <CustomerRouteSurface>
    <div role="status" aria-live="polite" className="flex min-h-[calc(100vh-124px)] items-center justify-center">Loading page…</div>
  </CustomerRouteSurface>;
}

// Call once at module scope. Each page downloads only after its route guards
// allow it to render. A manual retry replaces React.lazy's cached rejection;
// there is deliberately no automatic retry or document-reload loop.
export function lazyPage(loadPage) {
  function LazyPage(props) {
    const [attempt, setAttempt] = useState(() => ({ Page: lazy(loadPage), number: 0 }));
    const retry = () => setAttempt(current => ({ Page: lazy(loadPage), number: current.number + 1 }));
    const { Page, number } = attempt;
    return (
      <RoutePageBoundary key={number} onRetry={retry}>
        <Suspense fallback={<PageLoading />}>
          <Page {...props} />
        </Suspense>
      </RoutePageBoundary>
    );
  }
  return LazyPage;
}
